import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, Observable, forkJoin, EMPTY } from 'rxjs';
import { map, shareReplay, takeUntil, catchError, finalize, switchMap } from 'rxjs/operators';

import { environment } from './../../../../../environments/environment';

import { Category } from './../../../../models/category.model';
import { Product } from './../../../../models/product.model';
import { ProductUpdate } from './../../../../models/product-update.model';

import { AdminService } from './../../../../services/site/admin.service';
import { CategoryService } from './../../../../services/site/category.service';
import { LoadingService } from './../../../../shared/loading/loading.service';
import { ToastrService } from 'ngx-toastr';
import { TokenService } from './../../../../services/auth/token.service';

import { NgxSpinnerService } from "ngx-spinner";

export interface FileInfo {
  fileName?: string;
  fileType?: string;
  fileData?: string;
}

export interface CombinedCategory {
  id: number;
  name: string;
}

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss']
})
export class ProductDetailComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  
  product: Product | null = null;
  productForm: FormGroup;
  categories$: Observable<CombinedCategory[]> | null = null;
  
  readonly imageSource = `${environment.awsUrl}/products`;
  
  submitted = false;
  showProduct = false;
  uploadFile: FileInfo = {};
  isLoading = false;


  constructor(
    private readonly adminService: AdminService,
    private readonly categoryService: CategoryService,
    private readonly loadingService: LoadingService,
    private readonly tokenService: TokenService,
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly spinner: NgxSpinnerService
  ) {
    this.productForm = this.createProductForm();
  }

  ngOnInit(): void {
    // Check admin access first
    if (!this.tokenService.isAdmin()) {
      this.router.navigate(['/home']);
      return;
    }

    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupComponent();
  }

  get f() { 
    return this.productForm.controls; 
  }

  private initializeComponent(): void {
    this.route.params
      .pipe(
        takeUntil(this.destroy$),
        switchMap(params => {
          const productId = Number(params.id);
          console.log('Product ID from route:', productId);

          // if (!productId || isNaN(productId)) {
          //   this.handleError('Invalid product ID');
          //   return EMPTY;
          // }
          
          this.showSpinner();
          return this.loadProductData(productId);
        })
      )
      .subscribe({
        next: ([product, categories]) => {
          this.handleDataLoaded(product, categories);
        },
        error: (error) => {
          this.handleError('Failed to load product data', error);
        }
      });
  }

  private loadProductData(productId: number): Observable<[Product, CombinedCategory[]]> {
    const product$ = this.adminService.getProduct(productId)
      .pipe(shareReplay(1));
    
    const categories$ = this.getProcessedCategories();
    
    return forkJoin([product$, categories$])
      .pipe(
        finalize(() => this.hideSpinner())
      );
  }

  private getProcessedCategories(): Observable<CombinedCategory[]> {
    return this.categoryService.getCategories()
      .pipe(
        map(categories => this.transformCategories(categories)),
        shareReplay(1)
      );
  }

  private transformCategories(categories: Category[]): CombinedCategory[] {
    const finalCategories: CombinedCategory[] = [];
    
    categories.forEach(category => {
      // Add parent category
      finalCategories.push({
        id: category.id,
        name: category.name
      });
      
      // Add child categories with hierarchical names
      if (category.children?.length > 0) {
        category.children.forEach(child => {
          finalCategories.push({
            id: child.id,
            name: `${category.name} | ${child.name}`
          });
        });
      }
    });
    
    return finalCategories;
  }

  private handleDataLoaded(product: Product, categories: CombinedCategory[]): void {
    this.product = product;
    this.categories$ = new Observable(observer => {
      observer.next(categories);
      observer.complete();
    });
    
    this.setProductForm(product);
    this.showProduct = true;
  }

  private createProductForm(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required]],
      description: [''],
      activeFlag: [true],
      weight: [0, [Validators.min(0)]],
      noShippingFlag: [false],
      priceLabel: [''],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      categoryId: [0, [Validators.required, Validators.min(1)]],
      file: ['']
    });
  }

  private setProductForm(product: Product): void {
    this.productForm.patchValue({
      name: product.name || '',
      description: product.description || '',
      activeFlag: product.activeFlag ?? true,
      weight: product.weight || 0,
      noShippingFlag: product.noShippingFlag ?? false,
      priceLabel: product.priceLabel || '',
      unitPrice: product.unitPrice || 0,
      categoryId: product.productCategoryID || 0
    });
  }

  submit(): void {
    console.log('Form submitted:', this.productForm.value);
    this.submitted = true;

    if (this.productForm.invalid) {
      this.markFormGroupTouched();
      console.log('Form is invalid, please check the fields');
      this.toastr.error('Please fill in all required fields correctly');
      return;
    }

    if (!this.product) {
      this.handleError('No product data available');
      console.error('No product data available to save');
      return;
    }

    this.showSpinner();
    this.isLoading = true;

    const productUpdate = this.createProductUpdate();
    
    this.adminService.saveProduct(productUpdate)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.hideSpinner();
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (savedProduct) => {
          this.product = savedProduct;
          this.toastr.success('Product saved successfully');
          this.submitted = false;
          console.log('Product saved:', savedProduct);
        },
        error: (error) => {
          this.handleError('Failed to save product', error);
          console.error('Error saving product:', error);
        }
      });
  }

  private createProductUpdate(): ProductUpdate {
    const formValue = this.productForm.value;
    
    return new ProductUpdate({
      id: this.product!.id,
      name: formValue.name,
      description: formValue.description,
      activeFlag: formValue.activeFlag,
      weight: Number(formValue.weight) || 0,
      noShippingFlag: formValue.noShippingFlag,
      priceLabel: formValue.priceLabel,
      unitPrice: Number(formValue.unitPrice) || 0,
      imagePath: this.uploadFile.fileName || this.product!.imagePath,
      imageFileType: this.uploadFile.fileType,
      imageFileData: this.uploadFile.fileData,
      productCategoryID: Number(formValue.categoryId) || 0
    });
  }

  onFileChange(files: FileList | null): void {
    if (!files || files.length === 0) {
      this.resetUploadFile();
      return;
    }

    const file = files[0];
    
    // Validate file
    if (!this.isValidFile(file)) {
      this.toastr.error('Please select a valid image file');
      this.resetUploadFile();
      return;
    }

    this.uploadFile = {
      fileName: file.name,
      fileType: file.type
    };

    this.convertFileToBase64(file);
  }

  private isValidFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    return validTypes.includes(file.type) && file.size <= maxSize;
  }

  private convertFileToBase64(file: File): void {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      this.uploadFile.fileData = event.target?.result as string;
    };
    
    reader.onerror = (error) => {
      console.error('File reading error:', error);
      this.toastr.error('Failed to read file');
      this.resetUploadFile();
    };
    
    reader.readAsDataURL(file);
  }

  private resetUploadFile(): void {
    this.uploadFile = {};
    this.productForm.patchValue({ file: '' });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.productForm.controls).forEach(key => {
      const control = this.productForm.get(key);
      control?.markAsTouched();
    });
  }

  private showSpinner(): void {
    this.spinner.show();
  }

  private hideSpinner(): void {
    this.spinner.hide();
  }

  private handleError(message: string, error?: any): void {
    console.error(message, error);
    this.toastr.error(message);
    this.hideSpinner();
  }

  private cleanupComponent(): void {
    this.productForm.reset();
    this.categories$ = null;
    this.product = null;
    this.showProduct = false;
    this.resetUploadFile();
    this.submitted = false;
    this.isLoading = false;
  }
}